import { Alert, Stack } from '@mui/material';
import { useEffect, useState } from 'react';
import { useSessionStorage } from 'usehooks-ts';

import { SS_MORE_INFO_SOURCE_TAB } from '@/constants/common';

import { useGetMinorDetailAndRequiredDocuments } from '@/hooks/minor/useGetminorDetailAndRequiredDocuments';
import { useAppDispatch, useAppSelector } from '@/hooks/redux';

import { selectLoggedInUser } from '@/redux/auth/selectors';
import { selectRightStartFeature } from '@/redux/configs';
import { selectOperationData, setOperationData } from '@/redux/operation';
import { selectUserData } from '@/redux/user-data';

import {
  isAdultUser,
  isIndividualUser,
  isRemediationCustomer,
} from '@/utils/user';

import { FullPageLoaderOverlayWithWhiteBackground } from '../Layout/FullPageLoader';
import { MoreInformationForm } from '../PageForm/MoreInfoForm';
import {
  AdultChildToggle,
  TabEnum,
} from '../PageForm/MoreInfoForm/components/AdultChildToggle';
import { MinorMoreInformationForm } from '../PageForm/MoreInfoForm/MinorMoreInformationForm';

export const MoreInformationContainer = () => {
  const dispatch = useAppDispatch();
  const loggedUser = useAppSelector(selectLoggedInUser);
  const [moreInfoSourceTab, setMoreInfoSourceTab] = useSessionStorage<
    TabEnum | ''
  >(SS_MORE_INFO_SOURCE_TAB, '');
  const { shouldRefreshMinorData } = useAppSelector(selectOperationData);
  const enabledRightStart = useAppSelector(selectRightStartFeature);
  const { rawRequiredSections, accountTypes } = useAppSelector(selectUserData);
  const [tab, setTab] = useState<TabEnum>(() => {
    if (moreInfoSourceTab === TabEnum.Adult) return TabEnum.Adult;
    if (moreInfoSourceTab === TabEnum.Minor) return TabEnum.Minor;
    return TabEnum.Minor;
  });

  useEffect(() => {
    if (moreInfoSourceTab) {
      setMoreInfoSourceTab('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isAdultJointAccount = isAdultUser(loggedUser);

  const { data, refetch, isLoading } = useGetMinorDetailAndRequiredDocuments();

  useEffect(() => {
    if (!shouldRefreshMinorData) return;

    refetch().then(result => {
      if (result.data) {
        dispatch(
          setOperationData({
            user: result.data.user || null,
            requiredSections: result.data.requiredSections || [],
            accountTypes: accountTypes || [],
          }),
        );
      }
    });
  }, [shouldRefreshMinorData, refetch, dispatch, accountTypes]);

  useEffect(() => {
    if (data) {
      dispatch(
        setOperationData({
          user: data.user || null,
          requiredSections: data.requiredSections || [],
          accountTypes: accountTypes || [],
        }),
      );
    }
  }, [
    data,
    tab,
    isAdultJointAccount,
    rawRequiredSections,
    accountTypes,
    dispatch,
  ]);

  if (isLoading) {
    return <FullPageLoaderOverlayWithWhiteBackground />;
  }

  const handleTabChange = (value: TabEnum) => {
    // TODO: Correct checking to enable tab change https://rfhl.atlassian.net/browse/GDO-3529?focusedCommentId=66582
    // if (user?.infoAccuracyConfirmed) {
    setTab(value);
    // }
  };

  if (
    !enabledRightStart ||
    isIndividualUser(loggedUser) ||
    isRemediationCustomer(loggedUser)
  ) {
    return <MoreInformationForm />;
  }

  if (!isAdultJointAccount) {
    return (
      <Alert severity="error">
        Unsupported account type. Please contact support.
      </Alert>
    );
  }

  return (
    <>
      <Stack justifyContent="center" direction={'column'} width={'100%'}>
        <AdultChildToggle value={tab} onChange={handleTabChange} />
      </Stack>
      {tab === TabEnum.Adult && <MoreInformationForm />}
      {tab === TabEnum.Minor && <MinorMoreInformationForm />}
    </>
  );
};
